/**
 * Persist pending register invoice payment across register page refresh / navigation.
 * Scoped by location and register (when known), same pattern as bar tab mode session.
 */

export const REGISTER_PENDING_INVOICE_PAYMENT_KEY = 'blu_register_pending_invoice_payment_v1'
const SESSION_VERSION = 1

function canUseSessionStorage () {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'
}

function normalizeId (value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function normalizeApplications (applications) {
  if (!Array.isArray(applications)) return []
  return applications
    .map((a) => ({
      invoiceId: normalizeId(a?.invoiceId ?? a?.invoice_id),
      amountMinor: Number(a?.amountMinor ?? a?.amount_minor) || 0
    }))
    .filter((a) => a.invoiceId && a.amountMinor > 0)
}

/**
 * @param {unknown} raw
 * @returns {object|null}
 */
export function parseRegisterPendingInvoicePaymentSession (raw) {
  if (!raw || typeof raw !== 'object') return null

  const applications = normalizeApplications(raw.applications)
  const amountMinor = Number(raw.amountMinor) || applications.reduce((s, a) => s + a.amountMinor, 0)
  if (amountMinor <= 0 || !applications.length) return null

  return {
    v: SESSION_VERSION,
    locationId: normalizeId(raw.locationId),
    registerId: normalizeId(raw.registerId),
    customerId: normalizeId(raw.customerId),
    amountMinor,
    applications
  }
}

/**
 * @param {{ locationId?: number|null, registerId?: number|null }} context
 * @returns {object|null}
 */
export function readRegisterPendingInvoicePaymentSession (context = {}) {
  if (!canUseSessionStorage()) return null
  try {
    const raw = window.sessionStorage.getItem(REGISTER_PENDING_INVOICE_PAYMENT_KEY)
    if (!raw) return null
    const parsed = parseRegisterPendingInvoicePaymentSession(JSON.parse(raw))
    if (!parsed) return null

    const locationId = normalizeId(context.locationId)
    if (parsed.locationId && locationId && parsed.locationId !== locationId) {
      return null
    }

    const registerId = normalizeId(context.registerId)
    if (parsed.registerId && registerId && parsed.registerId !== registerId) {
      return null
    }

    return {
      customerId: parsed.customerId,
      amountMinor: parsed.amountMinor,
      applications: parsed.applications
    }
  } catch {
    return null
  }
}

/**
 * @param {{ customerId?: number|null, amountMinor?: number, applications?: Array }} context
 * @param {{ locationId?: number|null, registerId?: number|null }} scope
 */
export function writeRegisterPendingInvoicePaymentSession (context, scope = {}) {
  if (!canUseSessionStorage() || !context) return

  const applications = normalizeApplications(context.applications)
  const amountMinor = Number(context.amountMinor) || applications.reduce((s, a) => s + a.amountMinor, 0)
  if (amountMinor <= 0 || !applications.length) return

  const payload = {
    v: SESSION_VERSION,
    locationId: normalizeId(scope.locationId),
    registerId: normalizeId(scope.registerId),
    customerId: normalizeId(context.customerId),
    amountMinor,
    applications
  }

  try {
    window.sessionStorage.setItem(REGISTER_PENDING_INVOICE_PAYMENT_KEY, JSON.stringify(payload))
  } catch {
    // private mode / quota
  }
}

export function clearRegisterPendingInvoicePaymentSession () {
  if (!canUseSessionStorage()) return
  try {
    window.sessionStorage.removeItem(REGISTER_PENDING_INVOICE_PAYMENT_KEY)
  } catch {
    // ignore
  }
}
